import { useState, useCallback, useEffect } from "react";
import { useClinic } from "@/contexts/ClinicContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Receipt, ChevronDown, ChevronUp, Search } from "lucide-react";
import {
  fetchBillingClaims,
  BillingClaimRow,
  updateClaimStatus,
  ClaimStatus,
  CLAIM_STATUS_LABELS,
} from "@/services/insurance-authorization-service";
import { ClaimDocumentsBlock } from "@/components/claim-documents/ClaimDocumentsBlock";

// Statuses visíveis em Faturamento (ASSINADO em diante)
const BILLING_STATUSES: ClaimStatus[] = [
  "ASSINADO",
  "EM_ANALISE",
  "GLOSADO",
  "GLOSADO_PERDA",
  "GLOSA_RECURSO",
  "EM_RECEBIMENTO",
  "PAGO_CONVENIO",
  "NAO_PAGO_CONVENIO",
  "PARCIAL",
];

// Transições válidas (espelha VALID_TRANSITIONS do backend, recortado para o fluxo do faturamento)
const NEXT_STATUSES: Partial<Record<ClaimStatus, ClaimStatus[]>> = {
  ASSINADO: ["EM_ANALISE", "NEGADO"],
  EM_ANALISE: ["GLOSADO", "EM_RECEBIMENTO"],
  GLOSADO: ["GLOSADO_PERDA", "GLOSA_RECURSO"],
  GLOSA_RECURSO: ["GLOSADO", "EM_RECEBIMENTO"],
  EM_RECEBIMENTO: ["PAGO_CONVENIO", "NAO_PAGO_CONVENIO", "PARCIAL"],
};

const STATUS_COLORS: Partial<Record<ClaimStatus, string>> = {
  ASSINADO: "bg-blue-100 text-blue-800 border-blue-200",
  EM_ANALISE: "bg-purple-100 text-purple-800 border-purple-200",
  GLOSADO: "bg-orange-100 text-orange-800 border-orange-200",
  GLOSADO_PERDA: "bg-red-100 text-red-800 border-red-200",
  GLOSA_RECURSO: "bg-orange-100 text-orange-800 border-orange-200",
  EM_RECEBIMENTO: "bg-cyan-100 text-cyan-800 border-cyan-200",
  PAGO_CONVENIO: "bg-green-100 text-green-800 border-green-200",
  NAO_PAGO_CONVENIO: "bg-red-100 text-red-800 border-red-200",
  PARCIAL: "bg-yellow-100 text-yellow-800 border-yellow-200",
  NEGADO: "bg-red-100 text-red-800 border-red-200",
};

function StatusBadge({ status }: { status: ClaimStatus }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${STATUS_COLORS[status] || "bg-muted text-muted-foreground"}`}>
      {CLAIM_STATUS_LABELS[status]}
    </span>
  );
}

function BillingRow({ item, onChanged }: { item: BillingClaimRow; onChanged: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [newStatus, setNewStatus] = useState<ClaimStatus | "">("");
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  const nextOptions = NEXT_STATUSES[item.claim_status] ?? [];

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      await updateClaimStatus(item.id, newStatus, notes || undefined);
      toast({ title: "Status atualizado!" });
      setNewStatus("");
      setNotes("");
      onChanged();
    } catch (error) {
      toast({
        title: "Erro ao atualizar status",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });

  return (
    <div className="border border-border rounded-lg bg-card text-xs">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col min-w-0">
            <span className="font-medium truncate">
              {item.patient_name || "Paciente"}
              {item.claim_number && (
                <span className="ml-2 text-muted-foreground font-mono text-[10px]">#{item.claim_number}</span>
              )}
            </span>
            <span className="text-muted-foreground text-[10px]">
              {item.appointment_date
                ? `${item.appointment_date.split("-").reverse().join("/")} ${item.appointment_time ?? ""}`
                : formatDate(item.created_at)}
              {` · ${item.dental_insurance_name || "—"}`}
              {` · ${item.procedures_count} proc.`}
              {item.documents_count > 0 && ` · ${item.documents_count} doc.`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={item.claim_status} />
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-3 space-y-4 bg-muted/20">
          {/* Transição de status com observação */}
          {nextOptions.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Atualizar status</p>
              <div className="flex items-start gap-2 flex-wrap">
                <Select value={newStatus} onValueChange={(v) => setNewStatus(v as ClaimStatus)}>
                  <SelectTrigger className="h-8 text-xs w-[200px]">
                    <SelectValue placeholder="Novo status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {nextOptions.map((s) => (
                      <SelectItem key={s} value={s}>{CLAIM_STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  placeholder="Observação (opcional)..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="text-xs min-h-[32px] flex-1 min-w-[240px]"
                  rows={1}
                />
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleUpdateStatus}
                  disabled={!newStatus || updating}
                >
                  {updating ? "Salvando..." : "Atualizar"}
                </Button>
              </div>
            </div>
          )}

          <ClaimDocumentsBlock
            claimId={item.id}
            onChanged={onChanged}
          />
        </div>
      )}
    </div>
  );
}

export default function TissBillingPage() {
  const { selectedClinicId } = useClinic();
  const { toast } = useToast();

  const [items, setItems] = useState<BillingClaimRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | "ALL">("ALL");
  const [nameInput, setNameInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    if (!selectedClinicId) return;
    setLoading(true);
    try {
      const result = await fetchBillingClaims(selectedClinicId, page, PAGE_SIZE, {
        patientName: nameFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        claimStatus: statusFilter !== "ALL" ? statusFilter : undefined,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      toast({
        title: "Erro ao carregar faturamento",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedClinicId, page, nameFilter, dateFrom, dateTo, statusFilter, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [statusFilter, nameFilter, dateFrom, dateTo]);

  const applyNameFilter = () => setNameFilter(nameInput.trim());
  const clearFilters = () => {
    setNameInput("");
    setNameFilter("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("ALL");
  };

  return (
    <div className="page-wrapper space-y-4 max-w-5xl mx-auto">
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <Receipt className="page-icon" />
            <h1 className="page-title">Faturamento Convênios</h1>
            {total > 0 && (
              <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono text-muted-foreground">
                {total}
              </span>
            )}
          </div>
          <p className="page-subtitle">
            Gerencie guias em processo de faturamento — troca de status, observações e anexos
          </p>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Paciente</label>
            <Input
              placeholder="Parte do nome..."
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={applyNameFilter}
              onKeyDown={(e) => e.key === "Enter" && applyNameFilter()}
              className="h-7 text-xs w-[200px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">De</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 text-xs w-[140px]" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Até</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 text-xs w-[140px]" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">Status</label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ClaimStatus | "ALL")}>
              <SelectTrigger className="h-7 text-xs w-[180px]">
                <SelectValue placeholder="Filtrar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos (Faturamento)</SelectItem>
                {BILLING_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{CLAIM_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" className="btn-header gap-1.5" onClick={() => load()} disabled={loading}>
            <Search className={`btn-header-icon ${loading ? "animate-spin" : ""}`} /> Filtrar
          </Button>
          <Button variant="outline" size="sm" className="btn-header" onClick={clearFilters}>
            Limpar
          </Button>
        </div>
      </div>

      {!selectedClinicId && (
        <p className="text-xs text-muted-foreground text-center py-8">Selecione uma clínica no topo da tela.</p>
      )}

      {selectedClinicId && (
        <>
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">Nenhuma guia em faturamento.</p>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <BillingRow key={it.id} item={it} onChanged={load} />
              ))}
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading}>
                Anterior
              </Button>
              <span className="text-xs text-muted-foreground self-center">
                Página {page} de {Math.ceil(total / PAGE_SIZE)}
              </span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= Math.ceil(total / PAGE_SIZE) || loading}>
                Próxima
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
